import { Container, Title, Text, Stack, Modal, Badge } from "@mantine/core";
import { useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  useStainProviders,
  useCreateStainProvider,
  useUpdateStainProvider,
} from "../../../hooks/admin/useStains";
import AdminDataTable, {
  AdminColumn,
} from "../../../components/admin/shared/AdminDataTable";
import StainProviderForm, {
  StainProviderFormValues,
} from "../../../components/admin/stain-providers/StainProviderForm";
import { StainProvider } from "../../../services/admin/stains.service";

export default function StainProvidersAdminPage() {
  const [opened, { open, close }] = useDisclosure(false);
  const [editingProvider, setEditingProvider] = useState<StainProvider | null>(
    null
  );

  const { data: providers = [], isLoading } = useStainProviders();
  const createProvider = useCreateStainProvider();
  const updateProvider = useUpdateStainProvider();

  const handleAdd = () => {
    setEditingProvider(null);
    open();
  };

  const handleEdit = (item: StainProvider) => {
    setEditingProvider(item);
    open();
  };

  const handleSubmit = async (values: StainProviderFormValues) => {
    const payload = {
      ...values,
      averageTurnaroundDays:
        values.averageTurnaroundDays === ""
          ? undefined
          : values.averageTurnaroundDays,
    };

    if (editingProvider) {
      await updateProvider.mutateAsync({ id: editingProvider.id, ...payload });
    } else {
      await createProvider.mutateAsync(payload);
    }
    close();
  };

  const handleToggleActive = async (item: StainProvider) => {
    await updateProvider.mutateAsync({
      id: item.id,
      isActive: !item.isActive,
    });
  };

  const columns: AdminColumn<StainProvider>[] = [
    {
      key: "name",
      label: "Provider",
      sortable: true,
      render: (item) => (
        <Stack gap={0}>
          <Text size="sm" fw={500}>
            {item.name}
          </Text>
          {item.contactName && (
            <Text size="xs" c="dimmed">
              {item.contactName}
            </Text>
          )}
        </Stack>
      ),
    },
    {
      key: "email",
      label: "Contact",
      render: (item) => (
        <Stack gap={0}>
          {item.email && <Text size="sm">{item.email}</Text>}
          {item.primaryPhone && (
            <Text size="xs" c="dimmed">
              {item.primaryPhone}
            </Text>
          )}
        </Stack>
      ),
    },
    {
      key: "fax",
      label: "Fax",
      render: (item) => <Text size="sm">{item.fax || "—"}</Text>,
    },
    {
      key: "averageTurnaroundDays",
      label: "Turnaround",
      width: "120px",
      render: (item) => (
        <Text size="sm">
          {item.averageTurnaroundDays
            ? `${item.averageTurnaroundDays} days`
            : "—"}
        </Text>
      ),
    },
    {
      key: "isActive",
      label: "Status",
      width: "100px",
      render: (item) => (
        <Badge color={item.isActive ? "green" : "red"} variant="light">
          {item.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>Stain Providers</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Manage external stain providers and their capabilities
          </Text>
        </div>

        <AdminDataTable
          data={providers}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onToggleActive={handleToggleActive}
          getId={(item) => item.id}
          getIsActive={(item) => item.isActive}
          searchPlaceholder="Search providers..."
          emptyMessage="No stain providers configured"
        />
      </Stack>

      <Modal
        opened={opened}
        onClose={close}
        title={editingProvider ? "Edit Stain Provider" : "Add Stain Provider"}
        size="lg"
        closeOnClickOutside={false}
      >
        <StainProviderForm
          initialValues={editingProvider || undefined}
          onSubmit={handleSubmit}
          onCancel={close}
          submitLabel={editingProvider ? "Update" : "Create"}
          isSubmitting={createProvider.isPending || updateProvider.isPending}
        />
      </Modal>
    </Container>
  );
}
